const moment = require('moment');

const PLANNING_WEEK_START_HOUR = 7;
const PLANNING_WEEK_END_HOUR = 7;

function fractionalHour(m) {
    return m.hour() + m.minute() / 60 + m.second() / 3600;
}

function normalizeOpHours(operationHours) {
    const opHrs = Number(operationHours);
    if (!Number.isFinite(opHrs) || opHrs <= 0) return 24;
    return opHrs;
}

/**
 * Monday 07:00 of the work week that contains moment m.
 * Sat >= 07:00 and Sunday belong to the upcoming Monday.
 */
function getWorkWeekMonday7(m) {
    const day = m.clone().startOf('day');
    const dow = day.day();

    if (dow === 0) {
        return day.add(1, 'day').hour(PLANNING_WEEK_START_HOUR).minute(0).second(0).millisecond(0);
    }

    if (dow === 6 && fractionalHour(m) >= PLANNING_WEEK_END_HOUR) {
        return day.add(2, 'day').hour(PLANNING_WEEK_START_HOUR).minute(0).second(0).millisecond(0);
    }

    return day.day(1).hour(PLANNING_WEEK_START_HOUR).minute(0).second(0).millisecond(0);
}

/**
 * Hard week end: Saturday 07:00 after the Monday of this work week.
 */
function getWorkWeekEnd(m) {
    return getWorkWeekMonday7(m).clone().add(5, 'days'); // Mon 7 + 5 days = Sat 7
}

/**
 * Daily shift window for a Mon–Fri date: [day 07:00, day 07:00 + operationHours),
 * capped at Saturday 07:00 of that work week.
 * With operationHours=24, Fri 07:00 → Sat 07:00 (full Mon 7–Sat 7 continuous week).
 */
function getDayWorkWindow(dayMoment, operationHours = 24) {
    const opHrs = normalizeOpHours(operationHours);
    const start = dayMoment.clone().startOf('day').hour(PLANNING_WEEK_START_HOUR).minute(0).second(0).millisecond(0);
    const weekEnd = getWorkWeekEnd(start);
    let end = start.clone().add(opHrs, 'hours');
    if (end.isAfter(weekEnd)) {
        end = weekEnd.clone();
    }
    return { start, end };
}

function forEachWorkDayWindow(fromMoment, operationHours, callback) {
    let weekMonday = getWorkWeekMonday7(fromMoment);
    let guard = 0;

    while (guard < 520) {
        guard += 1;
        for (let i = 0; i < 5; i += 1) {
            const day = weekMonday.clone().add(i, 'days');
            const window = getDayWorkWindow(day, operationHours);
            if (window.end.isSameOrBefore(window.start)) {
                continue;
            }
            const stop = callback(window);
            if (stop) {
                return;
            }
        }
        weekMonday = weekMonday.clone().add(1, 'week');
    }
}

/**
 * True when m falls inside a daily operationHours shift within Mon 7:00–Sat 7:00.
 */
function isPlanningWorkingMoment(m, operationHours = 24) {
    const opHrs = normalizeOpHours(operationHours);
    const t = moment(m);
    let found = false;

    forEachWorkDayWindow(t, opHrs, (window) => {
        if (t.isSameOrAfter(window.start) && t.isBefore(window.end)) {
            found = true;
            return true;
        }
        if (window.start.isAfter(t)) {
            return true;
        }
        return false;
    });

    return found;
}

function snapToNextPlanningWorkStart(m, operationHours = 24) {
    const opHrs = normalizeOpHours(operationHours);
    const t = moment(m);

    if (isPlanningWorkingMoment(t, opHrs)) {
        return t.clone();
    }

    let next = null;
    forEachWorkDayWindow(t, opHrs, (window) => {
        if (window.end.isSameOrBefore(t)) {
            return false;
        }
        if (t.isBefore(window.start)) {
            next = window.start.clone();
            return true;
        }
        if (t.isSameOrAfter(window.start) && t.isBefore(window.end)) {
            next = t.clone();
            return true;
        }
        return false;
    });

    return next || t.clone();
}

function hoursUntilDayWorkEnd(m, operationHours = 24) {
    const opHrs = normalizeOpHours(operationHours);
    const t = moment(m);
    let hoursLeft = 0;

    forEachWorkDayWindow(t, opHrs, (window) => {
        if (t.isSameOrAfter(window.start) && t.isBefore(window.end)) {
            hoursLeft = window.end.diff(t, 'hours', true);
            return true;
        }
        return false;
    });

    return Math.max(0, hoursLeft);
}

function normalizePlanningStart(start, operationHours = 24) {
    return snapToNextPlanningWorkStart(moment(start), operationHours);
}

/**
 * Pure machine hours from qty × cycle (before Operation Hours scaling).
 */
function calcProductionHours(jsQuantity, cycleTime) {
    const qty = Number(jsQuantity) || 0;
    const cycle = Number(cycleTime) || 0;
    return Math.max(0, qty * cycle / 3600);
}

function calculateMaterialConsumptionKg(jsQuantity, partWeight) {
    const quantity = Number(jsQuantity);
    const weight = Number(partWeight);

    if (!Number.isFinite(quantity) || !Number.isFinite(weight)) {
        return 0;
    }

    return quantity * weight;
}

function formatMaterialConsumptionKg(jsQuantity, partWeight) {
    return `${calculateMaterialConsumptionKg(jsQuantity, partWeight).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} KG`;
}

/**
 * Time required to complete at the given Operation Hours / day:
 * productionHours × (24 / operationHours).
 */
function calcCompleteTimeHours(jsQuantity, cycleTime, operationHours = 24) {
    const opHrs = normalizeOpHours(operationHours);
    return Math.round(calcProductionHours(jsQuantity, cycleTime) * (24 / opHrs)) || 1;
}

/**
 * Remaining time required at the given Operation Hours / day.
 */
function calcRemainingTimeHours(jsQuantity, totalOutput, cycleTime, operationHours = 24) {
    const qty = Number(jsQuantity) || 0;
    const output = Number(totalOutput) || 0;
    const cycle = Number(cycleTime) || 0;
    const opHrs = normalizeOpHours(operationHours);
    return Math.round((qty - output) * cycle / 3600 * (24 / opHrs));
}

/**
 * Convert displayed Complete/Remaining (scaled by 24/opHrs) back to machine hours
 * for packing into daily Mon–Sat shifts.
 */
function productionHoursFromCompleteTime(completeTimeHours, operationHours = 24) {
    const opHrs = normalizeOpHours(operationHours);
    return Math.max(0, Number(completeTimeHours) || 0) * (opHrs / 24);
}

function isDayWorkEndBoundary(m, operationHours = 24) {
    const opHrs = normalizeOpHours(operationHours);
    const t = moment(m);
    let isBoundary = false;

    forEachWorkDayWindow(t.clone().subtract(1, 'second'), opHrs, (window) => {
        if (Math.abs(t.diff(window.end, 'seconds')) < 1) {
            isBoundary = true;
            return true;
        }
        return false;
    });

    return isBoundary;
}

/**
 * Add production hours onto the calendar using daily operationHours caps
 * inside Monday 7:00 – Saturday 7:00. Nights outside each day's shift and
 * Sat 7:00 – Mon 7:00 are skipped.
 */
function addPlanningHours(start, hours, usePlanningWeek = true, operationHours = 24, preserveEarlyStart = false) {
    const opHrs = normalizeOpHours(operationHours);
    const initialStart = moment(start);
    const startsBeforeSeven = fractionalHour(initialStart) < PLANNING_WEEK_START_HOUR;
    let preserveEarlyResult = preserveEarlyStart && startsBeforeSeven && !isPlanningWorkingMoment(initialStart, opHrs);
    let result = usePlanningWeek
        ? (preserveEarlyResult ? initialStart.clone() : snapToNextPlanningWorkStart(initialStart, opHrs))
        : initialStart.clone();
    let remaining = Math.max(0, Number(hours) || 0);

    if (remaining === 0) {
        return result;
    }

    if (!usePlanningWeek) {
        return result.add(remaining, 'hours');
    }

    let guard = 0;
    while (remaining > 0 && guard < 100000) {
        guard += 1;

        if (!isPlanningWorkingMoment(result, opHrs)) {
            if (preserveEarlyResult) {
                // Honor the explicit early Start until the regular 7:00 AM window begins.
                const nextSeven = result.clone().startOf('day').hour(PLANNING_WEEK_START_HOUR).minute(0).second(0).millisecond(0);
                const hoursUntilSeven = nextSeven.diff(result, 'hours', true);
                const step = Math.min(remaining, Math.max(0, hoursUntilSeven));
                result.add(step, 'hours');
                remaining -= step;
                if (remaining <= 0) {
                    break;
                }
                preserveEarlyResult = false;
                continue;
            }
            result = snapToNextPlanningWorkStart(result, opHrs);
            continue;
        }

        const hoursLeftInWindow = hoursUntilDayWorkEnd(result, opHrs);
        const step = Math.min(remaining, hoursLeftInWindow > 0 ? hoursLeftInWindow : 1);
        result.add(step, 'hours');
        remaining -= step;
    }

    if (!isPlanningWorkingMoment(result, opHrs) && !isDayWorkEndBoundary(result, opHrs) && !preserveEarlyResult) {
        result = snapToNextPlanningWorkStart(result, opHrs);
    }

    return result;
}

/**
 * Count working hours between two timestamps using the same daily caps.
 */
function countPlanningWorkingHours(start, end, operationHours = 24) {
    const opHrs = normalizeOpHours(operationHours);
    let cursor = snapToNextPlanningWorkStart(moment(start), opHrs);
    const endM = moment(end);
    let hours = 0;
    let guard = 0;

    if (!endM.isAfter(cursor)) {
        return 1;
    }

    while (cursor.isBefore(endM) && guard < 100000) {
        guard += 1;

        if (!isPlanningWorkingMoment(cursor, opHrs)) {
            cursor = snapToNextPlanningWorkStart(cursor, opHrs);
            continue;
        }

        const hoursLeftInWindow = hoursUntilDayWorkEnd(cursor, opHrs);
        const hoursToEnd = endM.diff(cursor, 'hours', true);
        const step = Math.min(
            hoursLeftInWindow > 0 ? hoursLeftInWindow : 1,
            Math.max(0, hoursToEnd)
        );

        if (step <= 0) {
            break;
        }

        hours += step;
        cursor.add(step, 'hours');
    }

    return Math.max(1, Math.round(hours));
}

function formatPlanningStartDate(start, usePlanningWeek = true, operationHours = 24) {
    const m = moment(start);
    if (!usePlanningWeek) {
        return m.format('YYYY-MM-DD HH:mm:ss');
    }
    return normalizePlanningStart(m, operationHours).format('YYYY-MM-DD HH:mm:ss');
}

/**
 * Place End from machine/production hours (not the 24/opHrs scaled display value).
 * Each day only consumes up to operationHours within Monday 7:00 – Saturday 7:00.
 */
function formatPlanningEndDate(start, productionHours, usePlanningWeek = true, operationHours = 24, preserveEarlyStart = false) {
    return addPlanningHours(start, productionHours, usePlanningWeek, operationHours, preserveEarlyStart)
        .format('YYYY-MM-DD HH:mm:ss');
}

function composePlanningStart(dateValue, timeValue, operationHours = 24) {
    // Preserve the selected wall-clock time; work-window rules apply to duration calculation.
    const date = moment(dateValue).format('YYYY-MM-DD');
    const selectedTime = moment(timeValue);
    const candidate = `${date} ${selectedTime.format('HH:mm')}:00`;

    return moment(candidate).format('YYYY-MM-DD HH:mm:ss');
}

function planningTimestampToPickerTime(timestamp) {
    // Use a neutral date so DatePicker renders the persisted wall-clock time.
    const value = moment(timestamp, 'YYYY-MM-DD HH:mm:ss', true);
    if (!value.isValid()) return null;

    return new Date(2000, 0, 1, value.hour(), value.minute(), 0, 0);
}

function formatPlanningTime(timestamp) {
    // Display persisted timestamps in the modal's 12-hour format.
    const value = moment(timestamp, 'YYYY-MM-DD HH:mm:ss', true);
    return value.isValid() ? value.format('h:mm A') : '';
}

module.exports = {
    PLANNING_WEEK_START_HOUR,
    PLANNING_WEEK_END_HOUR,
    getDayWorkWindow,
    isPlanningWorkingMoment,
    snapToNextPlanningWorkStart,
    normalizePlanningStart,
    calcProductionHours,
    calculateMaterialConsumptionKg,
    formatMaterialConsumptionKg,
    calcCompleteTimeHours,
    calcRemainingTimeHours,
    productionHoursFromCompleteTime,
    countPlanningWorkingHours,
    addPlanningHours,
    formatPlanningStartDate,
    formatPlanningEndDate,
    composePlanningStart,
    planningTimestampToPickerTime,
    formatPlanningTime,
};
